import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useGoBack } from '../../../../../utils/appNavigationNames';
import useResource from '../../../../../hooks/api/useResource';
import {
    PrimaryRecordAllergy,
    PrimaryRecordCondition,
    PrimaryRecordImaging,
    PrimaryRecordMedication,
    PrimaryRecordProcedure,
    PrimaryRecordVaccination,
    PrimaryRecordVisit,
} from '../../../../../primary-record-types/MedicalDataTypes';
import { ResourceImageCreate } from '../../../../../primary-record-types/apiTypes/medicalDataService';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import { getIntl } from '../../../../../utils/intl';
import ConditionForm from '../../../../shared/ResourceForms/ConditionForm';
import AllergyForm from '../../../../shared/ResourceForms/AllergyForm';
import MedicationForm from '../../../../shared/ResourceForms/MedicationForm';
import VisitForm from '../../../../shared/ResourceForms/VisitForm';
import { getCategoryLabel } from '../../../../../utils/resourceUtils/messages';
import VaccinationForm from '../../../../shared/ResourceForms/VaccinationForm';
import ProcedureForm from '../../../../shared/ResourceForms/ProcedureForm';
import ImagingForm from '../../../../shared/ResourceForms/ImagingForm';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';

const intl = getIntl('add-resource', {
    header: 'Create {category}',
});

export type AddDataScreenOwnProps = {};

export type AddDataScreenProps = AddDataScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'add-data'>;
};
const AddDataScreen: React.FC<AddDataScreenProps> = (props) => {
    const goBack = useGoBack();
    useSetHeaderTitle(intl('header', { category: getCategoryLabel(props.route.params.category) }));
    const { createResource, isCreatingResource } = useResource({
        medicalProfileId: props.route.params.profileId,
        resourceCategory: props.route.params.category,
        onCreated: () => {
            goBack();
        },
        relatedResourceCategories: [],
    });

    const renderForm = () => {
        const category = props.route.params.category;
        if (category === 'condition') {
            return (
                <ConditionForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        createResource({
                            images: data.imageUpdates
                                .filter((_) => _.type === 'create')
                                .map((_) => _ as ResourceImageCreate),
                            resource: data.resourceUpdate as PrimaryRecordCondition,
                            sourceResourceId: props.route.params.sourceResourceId || null,
                            careTeamMemberId: props.route.params.careTeamMemberId,
                        });
                    }}
                    isDisabled={isCreatingResource}
                    isLoading={isCreatingResource}
                    startDateTime={props.route.params.startDateTime}
                    onCancel={goBack}
                />
            );
        } else if (category === 'allergy') {
            return (
                <AllergyForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        createResource({
                            images: data.imageUpdates
                                .filter((_) => _.type === 'create')
                                .map((_) => _ as ResourceImageCreate),
                            resource: data.resourceUpdate as PrimaryRecordAllergy,
                            sourceResourceId: props.route.params.sourceResourceId || null,
                            careTeamMemberId: props.route.params.careTeamMemberId,
                        });
                    }}
                    isDisabled={isCreatingResource}
                    isLoading={isCreatingResource}
                    startDateTime={props.route.params.startDateTime}
                    onCancel={goBack}
                />
            );
        } else if (category === 'medication') {
            return (
                <MedicationForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        createResource({
                            images: data.imageUpdates
                                .filter((_) => _.type === 'create')
                                .map((_) => _ as ResourceImageCreate),
                            resource: data.resourceUpdate as PrimaryRecordMedication,
                            sourceResourceId: props.route.params.sourceResourceId || null,
                            careTeamMemberId: props.route.params.careTeamMemberId,
                        });
                    }}
                    isDisabled={isCreatingResource}
                    isLoading={isCreatingResource}
                    startDateTime={props.route.params.startDateTime}
                    onCancel={goBack}
                />
            );
        } else if (category === 'visit') {
            return (
                <VisitForm
                    profileId={props.route.params.profileId}
                    autoOpenWhenPicker
                    onSubmit={(data) => {
                        // A recurrence rule on the resource expands into its occurrences server-side.
                        createResource({
                            images: data.imageUpdates
                                .filter((_) => _.type === 'create')
                                .map((_) => _ as ResourceImageCreate),
                            resource: data.resourceUpdate as PrimaryRecordVisit,
                            sourceResourceId: props.route.params.sourceResourceId || null,
                            careTeamMemberId: props.route.params.careTeamMemberId,
                            sourceNoteId: data.sourceNoteId,
                        });
                    }}
                    isDisabled={isCreatingResource}
                    isLoading={isCreatingResource}
                    startDateTime={props.route.params.startDateTime}
                    onCancel={goBack}
                />
            );
        } else if (category === 'vaccination') {
            return (
                <VaccinationForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        createResource({
                            images: data.imageUpdates
                                .filter((_) => _.type === 'create')
                                .map((_) => _ as ResourceImageCreate),
                            resource: data.resourceUpdate as PrimaryRecordVaccination,
                            sourceResourceId: props.route.params.sourceResourceId || null,
                            careTeamMemberId: props.route.params.careTeamMemberId,
                        });
                    }}
                    isDisabled={isCreatingResource}
                    isLoading={isCreatingResource}
                    startDateTime={props.route.params.startDateTime}
                    medicalProfileId={props.route.params.profileId}
                    onCancel={goBack}
                />
            );
        } else if (category === 'procedure') {
            return (
                <ProcedureForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        createResource({
                            images: data.imageUpdates
                                .filter((_) => _.type === 'create')
                                .map((_) => _ as ResourceImageCreate),
                            resource: data.resourceUpdate as PrimaryRecordProcedure,
                            sourceResourceId: props.route.params.sourceResourceId || null,
                            careTeamMemberId: props.route.params.careTeamMemberId,
                        });
                    }}
                    isDisabled={isCreatingResource}
                    isLoading={isCreatingResource}
                    startDateTime={props.route.params.startDateTime}
                    onCancel={goBack}
                />
            );
        } else if (category === 'imaging') {
            return (
                <ImagingForm
                    profileId={props.route.params.profileId}
                    onSubmit={(data) => {
                        createResource({
                            images: data.imageUpdates
                                .filter((_) => _.type === 'create')
                                .map((_) => _ as ResourceImageCreate),
                            resource: data.resourceUpdate as PrimaryRecordImaging,
                            sourceResourceId: props.route.params.sourceResourceId || null,
                            careTeamMemberId: props.route.params.careTeamMemberId,
                        });
                    }}
                    isDisabled={isCreatingResource}
                    isLoading={isCreatingResource}
                    startDateTime={props.route.params.startDateTime}
                    onCancel={goBack}
                />
            );
        }

        return null;
    };

    return (
        <>
            <ProfileHeaderContainer profileId={props.route.params.profileId} bottomBorder omitChatButton>
                {renderForm()}
            </ProfileHeaderContainer>
        </>
    );
};

export default React.memo(AddDataScreen);
